import { useEffect, useState } from "react";
import { RISK, api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, History, Users2, ClipboardList } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

export function RiskBadge({ risk }) {
  const r = RISK[risk] || RISK.GREEN;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${r.cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${r.dot}`} /> {r.label}
    </span>
  );
}

export function MetricCards({ m }) {
  const u = m.unit || "berkas";
  const cards = [
    { label: "Target Volume", value: m.total_target, sub: u },
    { label: "Diproses", value: m.processed, sub: `${m.completion_pct}% selesai` },
    { label: "Sisa", value: m.remaining, sub: u },
    { label: "Estimasi Telat", value: m.estimated_delay_days, sub: "hari" },
  ];
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <Card key={c.label} className="p-4">
            <p className="uppercase text-[10px] tracking-wider font-semibold text-muted-foreground">{c.label}</p>
            <p className="font-heading text-2xl font-bold mt-1 tabular-nums">{c.value}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{c.sub}</p>
          </Card>
        ))}
      </div>
      {m.approved_count != null && (
        <p className="text-xs text-muted-foreground" data-testid="ml-measured-note">
          {m.approved_count} arsip disetujui = {m.processed} mL (otomatis)
        </p>
      )}
    </div>
  );
}

export function TargetActualChart({ m, project }) {
  const [prog, setProg] = useState(null);
  useEffect(() => {
    if (project?.id) api.get(`/projects/${project.id}/progress`).then((r) => setProg(r.data)).catch(() => {});
  }, [project?.id]);

  const summary = prog?.summary || m;
  const data = (prog?.series || []).map((p) => ({
    date: p.date.slice(5),
    Target: p.cumulative_target,
    Aktual: p.cumulative_actual,
    Forecast: p.forecast,
  }));

  return (
    <Card className="p-5" data-testid="target-actual-chart">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h3 className="font-heading font-semibold text-lg">Target vs Aktual vs Forecast</h3>
          <p className="text-xs text-muted-foreground">Kurva kumulatif harian — aktual = volume harian (mL) dari Laporan Volume Harian staff</p>
        </div>
        {summary?.trending_to_delay && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-700" data-testid="early-warning-badge">
            <AlertTriangle className="h-3.5 w-3.5" /> Peringatan Dini: tren menuju keterlambatan
          </span>
        )}
      </div>
      {data.length === 0 ? (
        <p className="text-sm text-muted-foreground py-10 text-center">Belum ada data progres (atur tanggal mulai & target proyek).</p>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={data} margin={{ left: -10, right: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" minTickGap={24} />
            <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
            <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="Target" stroke="hsl(var(--chart-1))" strokeWidth={2.5} dot={false} />
            <Line type="monotone" dataKey="Aktual" stroke="hsl(var(--chart-2))" strokeWidth={2.5} dot={false} connectNulls />
            <Line type="monotone" dataKey="Forecast" stroke="hsl(var(--chart-3))" strokeWidth={2} strokeDasharray="5 4" dot={false} connectNulls />
          </LineChart>
        </ResponsiveContainer>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-xs">
        <div><p className="text-muted-foreground">Produktivitas Saat Ini</p><p className="font-semibold tabular-nums">{summary?.current_productivity ?? "-"}/hari</p></div>
        <div><p className="text-muted-foreground">Produktivitas Dibutuhkan</p><p className="font-semibold tabular-nums">{summary?.required_productivity ?? "-"}/hari</p></div>
        <div><p className="text-muted-foreground">Estimasi Selesai</p><p className="font-semibold">{summary?.expected_completion || "-"}</p></div>
        <div><p className="text-muted-foreground">Estimasi Telat</p><p className="font-semibold tabular-nums">{summary?.estimated_delay_days ?? 0} hari</p></div>
      </div>
    </Card>
  );
}

export function StaffProductivityCard({ projectId }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/projects/${projectId}/staff-productivity`).then((r) => setData(r.data)).catch(() => {}); }, [projectId]);
  return (
    <Card className="p-5" data-testid="staff-productivity-card">
      <div className="flex items-center gap-2 mb-3">
        <Users2 className="h-4 w-4 text-primary" />
        <h3 className="font-heading font-semibold">Produktivitas Staff</h3>
        {data && <Badge variant="secondary" className="text-[10px]">Target {data.daily_target_arsip ?? data.daily_target} arsip/hari</Badge>}
      </div>
      {!data || data.staff.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">Belum ada staff ditugaskan.</p>
      ) : (
        <div className="divide-y divide-border">
          {data.staff.map((s) => {
            const st = s.status || "";
            const cls = st === "TARGET_ACHIEVED" ? "text-emerald-600 border-emerald-300"
              : st === "BELOW_TARGET" ? "text-amber-600 border-amber-300" : "text-red-600 border-red-300";
            const label = st === "TARGET_ACHIEVED" ? "Target Tercapai" : st === "BELOW_TARGET" ? "Di Bawah Target" : "Produktivitas Rendah";
            return (
              <div key={s.user_id} className="flex items-center justify-between py-2.5" data-testid={`staff-prod-${s.user_id}`}>
                <div>
                  <p className="text-sm font-medium">{s.name}</p>
                  <p className="text-xs text-muted-foreground">Rata-rata {s.avg_arsip ?? 0} arsip/hari · Total {s.total_arsip ?? 0} arsip · {s.days_achieving ?? 0} hari capai target</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold tabular-nums">{s.approved_today ?? 0} arsip hari ini</p>
                  <p className="text-[11px] text-muted-foreground">≈ {s.avg_ml ?? 0} mL/hari</p>
                  <Badge variant="outline" className={`text-[10px] mt-0.5 ${cls}`}>{label}</Badge>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

export function ArchiveStatusCard({ projectId }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get(`/projects/${projectId}/archive-status`).then((r) => setS(r.data)).catch(() => {}); }, [projectId]);
  if (!s) return null;
  const items = [
    { label: "Disetujui", value: s.approved, cls: "text-emerald-600" },
    { label: "Menunggu Review", value: s.pending_review, cls: "text-amber-600" },
    { label: "Sedang Direview", value: s.in_review, cls: "text-red-600" },
    { label: "Diproses AI", value: s.processing, cls: "text-red-500" },
    { label: "Ditolak", value: s.rejected, cls: "text-red-600" },
  ];
  return (
    <Card className="p-5" data-testid="archive-status-card">
      <div className="flex items-center gap-2 mb-3">
        <ClipboardList className="h-4 w-4 text-primary" />
        <h3 className="font-heading font-semibold">Status Pemrosesan Arsip</h3>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {items.map((it) => (
          <div key={it.label} className="rounded-lg border border-border p-3">
            <p className={`font-heading text-2xl font-bold tabular-nums ${it.cls}`}>{it.value}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">{it.label}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function TargetHistoryCard({ projectId }) {
  const [hist, setHist] = useState(null);
  useEffect(() => { api.get(`/projects/${projectId}/target-history`).then((r) => setHist(r.data)).catch(() => {}); }, [projectId]);
  if (!hist || hist.length === 0) return null;
  const LABELS = { estimated_volume: "Target Volume", daily_target: "Target Harian", working_days: "Hari Kerja", start_date: "Tanggal Mulai", target_completion_date: "Target Selesai" };
  return (
    <Card className="p-5" data-testid="target-history-card">
      <div className="flex items-center gap-2 mb-3">
        <History className="h-4 w-4 text-primary" />
        <h3 className="font-heading font-semibold">Riwayat Perubahan Target</h3>
      </div>
      <div className="space-y-3">
        {hist.map((h) => (
          <div key={h.id} className="text-xs border-l-2 border-border pl-3">
            <p className="text-muted-foreground">{new Date(h.timestamp).toLocaleString("id-ID")} · oleh {h.changed_by}</p>
            <ul className="mt-1 space-y-0.5">
              {Object.entries(h.changes).map(([k, v]) => (
                <li key={k}><span className="font-medium">{LABELS[k] || k}:</span> <span className="text-muted-foreground line-through">{String(v.old ?? "-")}</span> → <span className="font-semibold">{String(v.new)}</span></li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function ProgressBar({ m }) {
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between text-xs">
        <span className="text-muted-foreground">Progress</span>
        <span className="font-semibold tabular-nums">{m.completion_pct}% / target {m.expected_pct}%</span>
      </div>
      <Progress value={m.completion_pct} className="h-2" />
    </div>
  );
}
